import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Phone, XCircle, CheckCircle2, ShieldAlert } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import NwflInboundLink from "./NwflInboundLink";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import { deniedClaims } from "../data/deniedClaims";
import { businessInfo } from "../mock";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "../utils/schemas";

const PILLAR = "/denied-insurance-claim-florida";

const DeniedClaimPage = () => {
  const { slug } = useParams();
  const page = deniedClaims[slug];
  if (!page) return <Navigate to="/" replace />;

  const siblings = Object.values(deniedClaims).filter((p) => p.slug !== slug);
  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Denied Insurance Claims", path: PILLAR },
      { name: `Denied ${page.peril} Claim`, path: `/${page.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs
        items={[
          { name: "Denied Insurance Claims", path: PILLAR },
          { name: `Denied ${page.peril} Claim`, path: `/${page.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Denied Claims · {page.peril}
            </span>
          </div>
          <h1
            className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="denied-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Get My Free Denial Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Why denied */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Why {page.peril} Claims Get Denied in Florida
          </h2>
          <ul className="mt-7 space-y-3">
            {page.denialReasons.map((r, i) => (
              <li key={i} className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4">
                <XCircle className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How we overturn it */}
      <section className="py-14 md:py-16 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            How We Get Denied {page.peril} Claims Paid
          </h2>
          <div className="mt-9 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", t: "Read the Denial", d: "We pull the denial letter and policy and pinpoint the exact reason and provisions the carrier relied on." },
              { n: "02", t: "Re-document", d: "We inspect and document the true cause and full extent of the loss with objective evidence." },
              { n: "03", t: "Rebuild the Claim", d: "We write an independent replacement-cost estimate that defeats the stated denial reason." },
              { n: "04", t: "Dispute & Escalate", d: "We file a formal supplemental/dispute and escalate to appraisal or mediation if needed." },
            ].map((s) => (
              <div key={s.n} className="bg-[#0F0F0F] p-6">
                <div className="text-[#E11D26] text-4xl font-black leading-none" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
                  {s.n}
                </div>
                <h3 className="mt-4 text-white font-black uppercase text-sm tracking-wide" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
                  {s.t}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={PILLAR}
              data-testid="denied-pillar-link"
              className="inline-flex items-center gap-2 bg-[#E11D26]/10 border border-[#E11D26]/50 hover:bg-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
            >
              <CheckCircle2 className="h-4 w-4" /> Denied Insurance Claims (Overview)
            </Link>
            {page.relatedService && (
              <Link
                to={page.relatedService.href}
                data-testid="denied-service-link"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
              >
                {page.relatedService.label}
                <ArrowRight className="h-4 w-4 text-[#E11D26]" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white uppercase" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
            Denied {page.peril} Claim FAQ
          </h2>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`denied-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">{f.answer}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-white/45 text-xs italic">
            This information is general and educational, not legal advice. Statutory
            deadlines and policy provisions vary; consult the current Florida Statutes,
            your policy, or an attorney for your situation.
          </p>
        </div>
      </section>

      {/* Cluster nav — other denied claim types */}
      <section className="py-14 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-black text-white uppercase" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
            Other Denied Claim Types
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  Denied {s.peril} Claim
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3 className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight" style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}>
            A denial isn&apos;t the final word.
          </h3>
          <Link
            to="/#contact"
            data-testid="denied-cta-final"
            className="inline-flex items-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
          >
            Get My Free Denial Review <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {slug.includes("hurricane") && <NwflInboundLink topic="denied" />}

      <Footer />
    </div>
  );
};

export default DeniedClaimPage;
